import React from "react";

import { Tooltip } from "minimum-ui/core";
import { color } from "minimum-ui/tokens/colors";
import styled, { css } from "styled-components";

import { StatusBadge } from "../StatusBadge/StatusBadge";
import { Typography } from "../Typography/Typography";
import { STATUS } from "../types";

interface Props {
  status: STATUS;
  hideTopRow?: boolean;
  statusLabel?: string;
  statusText?: string;
  type: string;
  title: string;
  items?: {
    name: string;
    value: string | React.ReactElement;
    tooltip?: string | null;
    tooltipPosition?: "top" | "bottom" | "left" | "right";
  }[];
  onClick?: () => void;
}

const statusBorder: Record<STATUS, string> = {
  [STATUS.SUCCESS]: color.green[300],
  [STATUS.WARN]: color.yellow[300],
  [STATUS.ERROR]: color.red[300],
  [STATUS.INFO]: color.blue[300],
  [STATUS.NONE]: color.neutral[300],
  [STATUS.NEUTRAL]: color.neutral[300],
  [STATUS.PURPLE]: color.purple[300],
};

export const StatusCard: React.FC<Props> = (props) => {
  const {
    status,
    statusLabel,
    statusText,
    type,
    title,
    hideTopRow,
    items,
    onClick,
  } = props;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    <Wrapper
      $status={status}
      $hideTopRow={hideTopRow}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <TopRow>
        {/* Header */}
        <Block flex={1}>
          <Typography variant="fieldTitle" color={color.neutral[400]}>
            {type}
          </Typography>
          <Typography variant="titleLarge">{title}</Typography>
        </Block>

        {/* Status */}
        {status !== STATUS.NONE && (
          <Block alignItems="flex-end">
            <Typography variant="fieldTitle" color={color.neutral[400]}>
              {statusLabel}
            </Typography>
            <StatusBadge status={status} statusText={statusText} />
          </Block>
        )}
      </TopRow>

      <HR />

      <ItemsWrapper>
        {items?.map((item) => (
          <React.Fragment key={item.name}>
            <Block>
              <Typography variant="fieldTitle" color={color.neutral[400]}>
                {item.name}
              </Typography>
              {item.tooltip ? (
                <Tooltip
                  title={item.tooltip}
                  placement={item.tooltipPosition ?? "right"}
                >
                  <Typography variant="bodyLarge">{item.value}</Typography>
                </Tooltip>
              ) : (
                <Typography variant="bodyLarge">{item.value}</Typography>
              )}
            </Block>
          </React.Fragment>
        ))}
      </ItemsWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $status: STATUS; $hideTopRow?: boolean }>`
  cursor: ${(props) =>
    props.$status !== STATUS.NONE || Boolean(props.onClick)
      ? "pointer"
      : "default"};
  display: flex;
  flex-direction: column;
  min-width: 50rem;
  width: 50rem;
  background: ${color.neutral[100]};
  padding: ${({ $hideTopRow }) =>
    $hideTopRow ? "2.5rem" : "1.5rem 2.5rem 2.5rem 2.5rem"};
  border-style: solid;
  border-color: ${(props) => statusBorder[props.$status]} ${color.neutral[300]}
    ${color.neutral[300]} ${color.neutral[300]};
  border-radius: 0.75rem;
  border-width: ${({ $hideTopRow }) =>
    $hideTopRow ? "1px" : "0.75rem 1px 1px 1px"};

  ${(props) =>
    (props.$status !== STATUS.NONE || Boolean(props.onClick)) &&
    css`
      :hover {
        box-shadow: 0 0 0.5rem ${color.neutral[300]};
        background: white;
      }
      :active {
        box-shadow: 0 -0.15rem 0.25rem ${color.neutral[300]};
        background: ${color.neutral[100]};
      }
      transition: background 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
    `}
`;

const TopRow = styled.div<{ marginTop?: string }>`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  margin-top: ${(props) => props.marginTop || "0"};
`;

const ItemsWrapper = styled.div<{ marginTop?: string }>`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: ${(props) => props.marginTop || "0"};
`;

const Block = styled.div<{ flex?: number; alignItems?: string }>`
  flex: ${(props) => props.flex || "unset"};
  display: flex;
  flex-direction: column;
  align-items: ${(props) => props.alignItems || "flex-start"};
`;

const HR = styled.hr`
  margin: 1rem 0;
  border: none;
  border-top: 1px solid ${color.neutral[300]};
`;
